import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import api from '../api/api';

const dateLabel = (value) => value ? value.split('-').reverse().join('/') : 'Not set';

export default function TherapistOrganisationModal({ user, clinics, onClose, onSaved }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    let active = true;
    api.get(`/organisation-memberships/${user.id}/`).then(({ data }) => {
      if (active) setDetails(data);
    }).catch(() => {
      if (active) setError('Could not load organisation controls. Your staff account needs organisation administrator permission.');
    });
    return () => { active = false; };
  }, [user.id]);
  const update = (field, value) => setDetails((current) => ({ ...current, [field]: value }));
  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true); setError('');
    try {
      await api.post(`/organisation-memberships/${user.id}/`, {
        clinic: details.clinic ? Number(details.clinic) : null,
        covered: details.covered, share_clients: details.share_clients, client_ids: selected,
      });
      onSaved(); onClose();
    } catch (err) {
      const data = err.response?.data;
      setError(typeof data?.detail === 'string' ? data.detail : 'Could not save organisation membership. Check the company subscription and selected clients, then try again.');
    } finally { setSaving(false); }
  };
  return <Modal show onHide={() => { if (!saving) onClose(); }} size="lg">
    <Modal.Header closeButton={!saving}><Modal.Title>Manage organisation: {user.first_name} {user.last_name}</Modal.Title></Modal.Header>
    <Form onSubmit={save}>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {!details && !error && <Spinner animation="border" role="status"><span className="visually-hidden">Loading organisation details</span></Spinner>}
        {details && <>
          <div className="bg-light rounded p-3 mb-3">
            <strong>Subscription and trial dates</strong>
            <dl className="row mb-0 mt-2">
              <dt className="col-sm-4">Access</dt><dd className="col-sm-8">{details.access_paused ? 'Paused' : 'Available'}</dd>
              <dt className="col-sm-4">Subscription</dt><dd className="col-sm-8">{details.billing_mode === 'clinic' ? 'Company' : details.billing_mode === 'individual' ? 'Individual' : 'Not set'} — {details.billing_state || 'Not set'}</dd>
              <dt className="col-sm-4">Start date</dt><dd className="col-sm-8">{dateLabel(details.start_date)}</dd>
              <dt className="col-sm-4">Trial end date</dt><dd className="col-sm-8">{dateLabel(details.trial_end_date)}</dd>
            </dl>
          </div>
          <fieldset disabled={saving}>
            <Form.Group className="mb-3" controlId="membership-clinic">
              <Form.Label>Organisation</Form.Label>
              <Form.Select value={details.clinic || ''} onChange={(event) => update('clinic', event.target.value)}>
                <option value="">Remove organisation membership</option>
                {clinics.map((clinic) => <option key={clinic.id} value={clinic.id}>{clinic.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Check id="membership-covered" className="mb-2" label="Covered by this company's subscription" checked={details.covered} onChange={(event) => update('covered', event.target.checked)} />
            <Form.Text className="d-block mb-3">Company coverage requires an active or sponsored company subscription. Joining without coverage keeps an individual trial's original dates.</Form.Text>
            <Form.Check id="membership-sharing" className="mb-3" label="Share this organisation's clients with this therapist" checked={details.share_clients} onChange={(event) => update('share_clients', event.target.checked)} />
            <h3 className="h6">Transfer trial clients and their programs</h3>
            <p className="small text-muted">Select only the private trial clients to move. Their programs move with them; unselected clients stay private.</p>
            {details.clients.length === 0 && <p>No private trial clients available to transfer.</p>}
            {details.clients.map((client) => <Form.Check key={client.id} id={`transfer-client-${client.id}`} className="mb-2" label={`${client.name} (${client.email}) — ${client.program_count} programs`} checked={selected.includes(client.id)} onChange={(event) => setSelected((current) => event.target.checked ? [...current, client.id] : current.filter((id) => id !== client.id))} />)}
          </fieldset>
        </>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={saving} onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={!details || saving}>{saving ? 'Saving…' : 'Save organisation'}</Button>
      </Modal.Footer>
    </Form>
  </Modal>;
}
